import { useState, useCallback } from 'react';
import { useTenant } from '../context/TenantContext.js';
import { affiliates as affApi, tenants as tenantsApi } from '../api/client.js';
import { useApi } from '../hooks/useApi.js';
import { KpiCard } from '../components/KpiCard.js';
import { Badge } from '../components/Badge.js';
import { Modal } from '../components/Modal.js';
import { LoadingSpinner } from '../components/LoadingSpinner.js';
import { ErrorMessage } from '../components/ErrorMessage.js';
import { EmptyState } from '../components/EmptyState.js';
import type { Affiliate } from '../types/index.js';

export function AffiliatesPage() {
  const { activeTenantId, activeTenant, refreshActiveTenant } = useTenant();
  const [createOpen, setCreateOpen] = useState(false);
  const [toggling, setToggling] = useState(false);

  const fetcher = useCallback(
    () => (activeTenantId ? affApi.list(activeTenantId) : Promise.resolve({ affiliates: [] as Affiliate[] })),
    [activeTenantId],
  );

  const { data, loading, error, refetch } = useApi(fetcher, [activeTenantId]);
  const affiliateList = data?.affiliates ?? [];

  const toggleAffiliates = async () => {
    if (!activeTenantId || !activeTenant) {
      console.warn('[affiliates] cannot toggle — no tenant loaded. activeTenantId:', activeTenantId, 'activeTenant:', activeTenant);
      alert('No tenant loaded. Please wait for the page to finish loading or select a tenant.');
      return;
    }
    setToggling(true);
    try {
      const newValue = !activeTenant.affiliateEnabled;
      console.log('[affiliates] toggling affiliateEnabled to:', newValue);
      await tenantsApi.update(activeTenantId, { affiliateEnabled: newValue });
      console.log('[affiliates] toggle succeeded');
      await refreshActiveTenant();
    } catch (err) {
      console.error('[affiliates] toggle failed:', err);
      alert('Failed to toggle affiliate system. Check the browser console for details.');
    } finally {
      setToggling(false);
    }
  };

  const handleDeactivate = async (aff: Affiliate) => {
    if (!activeTenantId) return;
    try {
      await affApi.delete(activeTenantId, aff.id);
      console.log('[affiliates] deactivated:', aff.code);
      await refetch();
    } catch (err) {
      console.error('[affiliates] deactivate failed:', err);
    }
  };

  const totalReferrals = affiliateList.reduce((sum, a) => sum + a.totalReferrals, 0);
  const totalEarned = affiliateList.reduce((sum, a) => sum + Number(a.totalEarned), 0);

  if (loading) return <LoadingSpinner text="Loading affiliates..." />;
  if (error) return <ErrorMessage message={error} onRetry={() => void refetch()} />;

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
        <div>
          <h2 style={{ fontFamily: 'var(--fd)', fontSize: 18, fontWeight: 700, letterSpacing: '-0.02em', marginBottom: 4 }}>Affiliates</h2>
          <p style={{ fontSize: 13, color: 'var(--t3)' }}>Manage your referral program and commissions</p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn-sm btn-ghost" onClick={() => void toggleAffiliates()} disabled={toggling}>
            {toggling ? 'Saving...' : activeTenant?.affiliateEnabled ? 'Disable' : 'Enable'} {toggling ? '' : 'Affiliates'}
          </button>
          {activeTenant?.affiliateEnabled && (
            <button className="btn-sm btn-primary" onClick={() => setCreateOpen(true)}>+ Add Affiliate</button>
          )}
        </div>
      </div>

      {!activeTenant?.affiliateEnabled ? (
        <EmptyState
          title="Affiliate system is disabled"
          description="Enable the affiliate system to start tracking referrals and commissions."
          action={<button className="btn-sm btn-primary" onClick={() => void toggleAffiliates()} disabled={toggling}>{toggling ? 'Enabling...' : 'Enable Affiliates'}</button>}
        />
      ) : (
        <>
          <div className="kpi-row" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
            <KpiCard
              label="Total Referrals"
              value={String(totalReferrals)}
              color="violet"
              icon={
                <svg viewBox="0 0 24 24" fill="none" stroke="var(--violet)" strokeWidth="2.5" style={{ width: 13, height: 13 }}>
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" />
                </svg>
              }
            />
            <KpiCard
              label="Commission Rate"
              value={`${activeTenant.affiliateCommissionPct}%`}
              color="teal"
              icon={
                <svg viewBox="0 0 24 24" fill="none" stroke="var(--teal)" strokeWidth="2.5" style={{ width: 13, height: 13 }}>
                  <line x1="19" y1="5" x2="5" y2="19" /><circle cx="6.5" cy="6.5" r="2.5" /><circle cx="17.5" cy="17.5" r="2.5" />
                </svg>
              }
            />
            <KpiCard
              label="Total Paid Out"
              value={`$${totalEarned.toFixed(2)}`}
              color="green"
              icon={
                <svg viewBox="0 0 24 24" fill="none" stroke="var(--green)" strokeWidth="2.5" style={{ width: 13, height: 13 }}>
                  <line x1="12" y1="1" x2="12" y2="23" /><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
                </svg>
              }
            />
          </div>

          {affiliateList.length === 0 ? (
            <EmptyState title="No affiliates yet" description="Add your first affiliate to start tracking referrals." />
          ) : (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Discord User</th>
                    <th>Code</th>
                    <th>Commission</th>
                    <th>Referrals</th>
                    <th>Earned</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {affiliateList.map((aff) => (
                    <tr key={aff.id}>
                      <td style={{ color: 'var(--t1)', fontWeight: 500 }}>{aff.discordUserId}</td>
                      <td style={{ fontFamily: 'monospace', fontSize: 12, color: 'var(--violet-bright)' }}>{aff.code}</td>
                      <td>{aff.commissionPct ?? activeTenant.affiliateCommissionPct}%</td>
                      <td>{aff.totalReferrals}</td>
                      <td style={{ fontFamily: 'var(--fd)', fontWeight: 600 }}>${Number(aff.totalEarned).toFixed(2)}</td>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <Badge status={aff.active ? 'active' : 'expired'} label={aff.active ? 'Active' : 'Inactive'} />
                          {aff.active && (
                            <button
                              className="btn-sm btn-ghost"
                              style={{ padding: '2px 8px', fontSize: 10 }}
                              onClick={() => void handleDeactivate(aff)}
                            >
                              Deactivate
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      <CreateAffiliateModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        tenantId={activeTenantId!}
        onCreated={() => void refetch()}
      />
    </>
  );
}

function CreateAffiliateModal({
  open,
  onClose,
  tenantId,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  tenantId: string;
  onCreated: () => void;
}) {
  const [discordUserId, setDiscordUserId] = useState('');
  const [code, setCode] = useState('');
  const [commissionPct, setCommissionPct] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!discordUserId) { setError('Discord User ID is required'); return; }
    setSaving(true);
    setError('');
    try {
      await affApi.create(tenantId, {
        discordUserId,
        code: code || undefined,
        commissionPct: commissionPct ? Number(commissionPct) : undefined,
      });
      console.log('[affiliates] created for user:', discordUserId);
      onCreated();
      onClose();
      setDiscordUserId('');
      setCode('');
      setCommissionPct('');
    } catch (err) {
      console.error('[affiliates] create failed:', err);
      setError(err instanceof Error ? err.message : 'Failed to create affiliate');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title="Add Affiliate">
      <form onSubmit={(e) => void handleSubmit(e)} className="modal-form">
        <div className="form-group">
          <label className="form-label">Discord User ID</label>
          <input className="form-input" value={discordUserId} onChange={(e) => setDiscordUserId(e.target.value)} placeholder="e.g. 1234567890" style={{ fontFamily: 'monospace' }} />
        </div>
        <div className="form-group">
          <label className="form-label">Referral Code (optional)</label>
          <input className="form-input" value={code} onChange={(e) => setCode(e.target.value)} placeholder="Auto-generated if empty" />
        </div>
        <div className="form-group">
          <label className="form-label">Commission % (optional — overrides tenant default)</label>
          <input className="form-input" type="number" min="0" max="100" step="0.01" value={commissionPct} onChange={(e) => setCommissionPct(e.target.value)} placeholder="Leave blank for default" />
        </div>
        {error && <div className="form-error">{error}</div>}
        <button type="submit" className="btn-sm btn-primary" style={{ width: '100%', padding: 12, fontSize: 14 }} disabled={saving}>
          {saving ? 'Creating...' : 'Create Affiliate'}
        </button>
      </form>
    </Modal>
  );
}
